import {useMoods} from '@/ts/composables/pure/use-moods';
import type {JournalFeelingOption} from '@/ts/types/component/today-page';
import type {PrimaryFeeling} from '@/ts/types/dto/interventions-dto';

/** Enough suggestions to fill the picker for a mood with no family of its own. */
const DEFAULT_FEELING_COUNT = 30;


/**
 * The fallback set, drawn one family at a time in turn.
 *
 * Flattening the families and taking the first thirty gave the first family's first thirty words —
 * with the CMS's own ordering that was thirty ways of saying angry, offered as the common set to
 * someone who had not picked a mood yet. Round-robin gives the mix the design draws: a few from
 * each of the six, so the list reads as a spread of feelings rather than one mood's thesaurus.
 */
function takeAcrossFamilies(families: JournalFeelingOption[][]): JournalFeelingOption[] {
  const picked: JournalFeelingOption[] = [];
  const longest = Math.max(0, ...families.map((family) => family.length));

  for (let index = 0; index < longest && picked.length < DEFAULT_FEELING_COUNT; index++) {
    for (const family of families) {
      if (picked.length >= DEFAULT_FEELING_COUNT) {
        break;
      }
      const feeling = family[index];
      // Names repeat across families, and two identical pills read as a rendering fault.
      if (feeling && !picked.some((chosen) => chosen.name === feeling.name)) {
        picked.push(feeling);
      }
    }
  }
  return picked;
}

/**
 * Turns the journal template's feeling tree into the suggestions a picker offers (IPD-3107).
 *
 * Only secondary feelings are offered. The primary families are the grouping, not choices: they
 * are the six broad words a mood already implies, so offering "happy" beside Joyful asks the user
 * to say the same thing twice.
 *
 * Lifted out of `use-journal-tile` so the Today tile and the Journal tab's own entry modal derive
 * their suggestions the same way rather than each inventing a list — the modal used to offer the
 * whole tree through a typeahead, unrelated to the mood on screen.
 */
export function useFeelingSuggestions(availableFeelings: PrimaryFeeling[]): FeelingSuggestions {
  const {moods, getMoodFeelingFamily} = useMoods();

  // Grouped by the primary's untranslated `key`, never its display name, which arrives translated.
  const byFamily = new Map<string, JournalFeelingOption[]>(
    availableFeelings.map((primary) => [
      // Falling back to the id keeps a keyless family out of every mood's suggestions while still
      // contributing to the fallback set below.
      primary.key ?? primary.id,
      primary.secondaryFeelings.map((secondary) => ({id: secondary.id, name: secondary.name})),
    ]),
  );

  return {
    byMood: Object.fromEntries(
      moods
        .map((mood) => [mood.key, byFamily.get(getMoodFeelingFamily(mood.key) ?? '') ?? []] as const)
        .filter(([, list]) => list.length > 0),
    ),
    fallback: takeAcrossFamilies([...byFamily.values()]),
  };
}

/** What a feelings picker offers, derived from the journal template's feeling tree. */
export interface FeelingSuggestions {
  /** Secondary feelings keyed by mood, so the suggestions relate to what the user picked. */
  byMood: Record<string, JournalFeelingOption[]>;
  /** Shown for a mood with no family of its own, and before any mood is chosen. */
  fallback: JournalFeelingOption[];
}
